import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import BaseModal from "./BaseModal";

export default function ProfileModal({ onClose, onOpenSecurity, onOpenSettings }) {
  const { t } = useTranslation();
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const doLogout = () => {
    logout();
    onClose?.();
    navigate("/login");
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        updateUser(user.id, { avatarDataUrl: imageData });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      await updateUser(user.id, {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        fullName: `${editForm.firstName.trim()} ${editForm.lastName.trim()}`.trim(),
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Profile update error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
    });
    setIsEditing(false);
  };

  return (
    <BaseModal
      title={t("profileModal.title")}
      onClose={onClose}
      footer={
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
          <button
            type="button"
            onClick={onOpenSettings}
            className="h-14 rounded-2xl border-2 border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all font-black text-[10px] uppercase tracking-widest text-slate-500 flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-[20px]">settings</span>
            {t("settings.title")}
          </button>
          <button
            type="button"
            onClick={onOpenSecurity}
            className="h-14 rounded-2xl border-2 border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all font-black text-[10px] uppercase tracking-widest text-slate-500 flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-[20px]">security</span>
            {t("security.title")}
          </button>
          <button
            type="button"
            onClick={doLogout}
            className="h-14 rounded-2xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white font-black text-[10px] uppercase tracking-[0.2em] transition-all sm:col-span-2 shadow-lg shadow-red-500/5 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-[20px]">logout</span>
            {t("profileModal.logout")}
          </button>
        </div>
      }
      maxWidthClass="max-w-md"
    >
      <div className="space-y-10 py-2">
        {/* Avatar Section */}
        <div className="flex flex-col items-center gap-6">
          <div className="relative group">
            <div className="h-28 w-28 rounded-[32px] overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center border-4 border-white dark:border-slate-900 shadow-2xl transition-transform group-hover:scale-105 duration-300">
              {(user?.avatarUrl || user?.avatarDataUrl) ? (
                <img 
                  src={user.avatarUrl || user.avatarDataUrl} 
                  alt="Avatar" 
                  className="h-full w-full object-cover" 
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700">
                  <span className="material-symbols-outlined text-slate-400 text-5xl">person</span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute -bottom-2 -right-2 h-10 w-10 rounded-xl bg-[#1e9f6b] hover:bg-[#0d7a52] text-white flex items-center justify-center shadow-xl shadow-[#1e9f6b]/30 transition-all active:scale-90"
            >
              <span className="material-symbols-outlined text-[18px]">add_a_photo</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
          <div className="text-center space-y-1">
            <h3 className="text-xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight">
              {user?.fullName || "-"}
            </h3>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 tracking-widest">
              {user?.phone || "-"}
            </p>
          </div>
        </div>

        {/* Info Section */}
        <div className="space-y-5">
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-[#1e9f6b]">
                <span className="material-symbols-outlined text-[16px]">info</span>
              </div>
              <h4 className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
                {t("profileModal.personalInfo")}
              </h4>
            </div>
            
            {!isEditing ? (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="text-[10px] font-black uppercase tracking-widest text-[#1e9f6b] hover:bg-[#1e9f6b]/10 px-3 py-1.5 rounded-lg transition-all"
              >
                {t("common.edit")}
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 px-3 py-1.5 rounded-lg transition-all"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleSaveProfile}
                  disabled={isLoading}
                  className="text-[10px] font-black uppercase tracking-widest text-white bg-[#1e9f6b] hover:bg-[#0d7a52] px-3 py-1.5 rounded-lg transition-all shadow-lg shadow-[#1e9f6b]/20 disabled:opacity-50"
                >
                  {isLoading ? t("common.saving") : t("common.save")}
                </button>
              </div>
            )}
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/40 rounded-[28px] border border-slate-100 dark:border-slate-800/50 p-6 space-y-6">
            {isEditing ? (
              <div className="space-y-5">
                <div className="space-y-2">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] px-1">
                    {t("auth.firstName")}
                  </label>
                  <input
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full h-12 bg-white dark:bg-slate-900 border-2 border-transparent focus:border-[#1e9f6b] dark:text-white rounded-2xl px-4 outline-none transition-all font-black italic shadow-sm"
                    placeholder={t("auth.firstName")}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] px-1">
                    {t("auth.lastName")}
                  </label>
                  <input
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full h-12 bg-white dark:bg-slate-900 border-2 border-transparent focus:border-[#1e9f6b] dark:text-white rounded-2xl px-4 outline-none transition-all font-black italic shadow-sm"
                    placeholder={t("auth.lastName")}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-center justify-between group">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("auth.firstName")}</span>
                  <span className="text-sm font-black text-slate-900 dark:text-white italic">{user?.firstName || "-"}</span>
                </div>
                <div className="flex items-center justify-between group">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("auth.lastName")}</span>
                  <span className="text-sm font-black text-slate-900 dark:text-white italic">{user?.lastName || "-"}</span>
                </div>
                <div className="flex items-center justify-between group border-t border-slate-100 dark:border-slate-800 pt-5">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("profileModal.phone")}</span>
                  <span className="text-sm font-bold text-slate-500 dark:text-slate-400 tracking-wide">{user?.phone || "-"}</span>
                </div>
                <div className="flex items-center justify-between group">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("profileModal.role")}</span>
                  <span className="text-[10px] font-black text-[#1e9f6b] uppercase italic bg-[#1e9f6b]/10 px-2 py-0.5 rounded-full">{user?.role || "-"}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </BaseModal>
  );
}


